import {
  useMutation,
  type FetchResult,
  type Reference,
  type StoreObject,
} from "@apollo/client";
import { DELETE_HEALTH_LEAF } from "../../../common/operations/mutations";
import { handleApolloError } from "../../../common/utils/handleApolloError";
// no client cache reset; we'll do targeted cache updates

interface DeleteHealthLeafParams {
  healthLeafPartitionKey?: string;
  healthLeafSortKey?: string;
}

interface DeleteHealthLeafVariables {
  input: {
    healthLeafPartitionKey?: string;
    healthLeafSortKey?: string;
  };
}

interface DeleteHealthLeafResultShape {
  deleteHealthLeaf: {
    deleted: boolean;
    pk1: string;
    sk: string;
  };
}

export const useDeleteHealthLeaf = () => {
  const [mutate, { data, loading }] = useMutation<
    DeleteHealthLeafResultShape,
    DeleteHealthLeafVariables
  >(DELETE_HEALTH_LEAF);

  const deleteHealthLeaf = async (
    params: DeleteHealthLeafParams
  ): Promise<FetchResult<DeleteHealthLeafResultShape>> => {
    const result = await mutate({
      variables: {
        input: {
          healthLeafPartitionKey: params.healthLeafPartitionKey,
          healthLeafSortKey: params.healthLeafSortKey,
        },
      },
      onError: handleApolloError,
      update(cache, { data }) {
        const pk = data?.deleteHealthLeaf?.pk1;
        const sk = data?.deleteHealthLeaf?.sk;

        if (!pk || !sk) return;

        cache.modify({
          fields: {
            listHealthLeaves(existing, { readField }) {
              if (!existing?.items) return existing;

              const items = existing.items || [];
              const filtered =
                items?.filter((item: Reference | StoreObject) => {
                  const itemSk = readField<string>("sk", item);
                  const itemPk =
                    readField<string>("pk", item) ??
                    readField<string>("pk1", item);

                  // Remove only matching PK/SK pair
                  return !(
                    itemSk === sk &&
                    (itemPk === pk || pk.endsWith("#HEALTH"))
                  );
                }) ?? items;

              return {
                ...existing,
                items: filtered,
              };
            },
          },
        });
      },
    });

    return result;
  };

  return { deleteHealthLeaf, data, loading };
};
